import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export const initState = {
  q1: 3,
  q2: 3,
  q3: [1, 2, 3, 4, 5],
  q4: 3,
  q5: 3,
  q6: 3,
  q7: 3,
  q8: 0,
};

export type QKey = keyof typeof initState;

const defaultValues = {
  values: initState,
  isComplete: false,
  inProgress: false,
  backToForm: (): void => undefined,
  startAssessment: (): void => undefined,
  setComplete: (): void => undefined,
  setValue: (key: QKey, value: number): void => undefined,
  retake: (str?: string): void => undefined,
};

export const AnswerContext = React.createContext(defaultValues);

export const AnswerProvider: React.FC<{ data: typeof initState }> = ({
  data,
  children,
}) => {
  const [context, setContext] = useState(data);
  const [isComplete, setIsComplete] = useState(false);
  const [inProgress, setInProgress] = useState(false);

  useEffect(() => {
    const keys = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8'] as const;
    const obj: any = {};
    keys.forEach((k) => {
      const item = sessionStorage.getItem(k);
      if (item) {
        obj[k] = JSON.parse(item);
      } else {
        obj[k] = initState[k];
      }
    });
    setContext(obj);
  }, []);

  useEffect(() => {
    const status = sessionStorage.getItem('complete');
    if (status === 'true') {
      setIsComplete(true);
    }
  }, []);

  useEffect(() => {
    const status = sessionStorage.getItem('inprogress');
    if (status === 'true') {
      setInProgress(true);
    }
  }, []);

  const setValue = useCallback((key: QKey, value: number | number[]) => {
    sessionStorage.setItem(key, JSON.stringify(value));
    setContext((x) => ({ ...x, [key]: value }));
  }, []);

  const setComplete = useCallback(() => {
    setIsComplete(true);
    sessionStorage.setItem('complete', 'true');
  }, []);

  const startAssessment = useCallback(() => {
    sessionStorage.setItem('inprogress', 'true');
    setInProgress(true);
  }, []);

  const backToForm = useCallback(() => {
    sessionStorage.removeItem('inprogress');
    setInProgress(false);
  }, []);

  const retake = useCallback((step = '0') => {
    setContext(initState);
    sessionStorage.setItem('showInsight', 'false');
    sessionStorage.removeItem('complete');
    sessionStorage.setItem('step', step);
    setIsComplete(false);
    setInProgress(true);
  }, []);

  const value = useMemo(
    () => ({
      isComplete,
      setComplete,
      values: context,
      setValue,
      startAssessment,
      inProgress,
      retake,
      backToForm,
    }),

    [
      isComplete,
      setComplete,
      context,
      setValue,
      startAssessment,
      inProgress,
      retake,
      backToForm,
    ],
  );

  return (
    <AnswerContext.Provider value={value}>{children}</AnswerContext.Provider>
  );
};

export const useAnswerContext = () => useContext(AnswerContext);
